import dayjs from 'dayjs';
import type { RoadmapInitiative } from '../../../api/strategy-client/strategy-client.type';
import { safeParseDateInput } from '../../../helpers/date-time-helpers';
import type { DeliveryProgressFilter, InitiativeBarPosition, TimelineBounds } from './roadmap.type';

/**
 * Helper function to check if initiative matches delivery progress filter
 * @param {number} progress - The progress of the initiative
 * @param {DeliveryProgressFilter} filter - The filter to apply
 * @returns {boolean} True if the initiative matches the filter, false otherwise
 */
const matchesDeliveryProgressFilter = (progress: number, filter: DeliveryProgressFilter): boolean => {
  if (progress === 0 && filter.notStarted) return true;
  if (progress > 0 && progress <= 74 && filter.inProgress) return true;
  if (progress >= 75 && progress < 100 && filter.nearComplete) return true;
  if (progress === 100 && filter.complete) return true;
  return false;
};

/**
 * Helper function to check if initiative overlaps with selected year
 * @param {string | null} startDate - The start date of the initiative
 * @param {string | null} endDate - The end date of the initiative
 * @param {number | null} selectedYear - The selected year to filter by (null means all years)
 * @returns {boolean} True if the initiative overlaps with the selected year, false otherwise
 */
const matchesYearFilter = (startDate: string | null, endDate: string | null, selectedYear: number | null): boolean => {
  // If no year is selected, show all initiatives
  if (!selectedYear) return true;

  // If initiative has no dates, show it
  if (!startDate && !endDate) return true;

  const start = startDate ? safeParseDateInput(startDate) : null;
  const end = endDate ? safeParseDateInput(endDate) : null;

  // Check if initiative overlaps with the selected year
  // Initiative overlaps if it starts before end of year and ends after start of year
  const yearStart = dayjs(`${selectedYear}-01-01`).startOf('year');
  const yearEnd = dayjs(`${selectedYear}-12-31`).endOf('year');

  // If only start date exists
  if (start && !end) {
    return !start.isBefore(yearStart) && !start.isAfter(yearEnd);
  }

  // If only end date exists
  if (!start && end) {
    return !end.isBefore(yearStart) && !end.isAfter(yearEnd);
  }

  // If both dates exist, check for overlap
  if (start && end) {
    return !(end.isBefore(yearStart) || start.isAfter(yearEnd));
  }

  return true;
};

/**
 * Calculate timeline bounds from initiatives
 * @param {Array<{outcomes: Array<{initiatives: RoadmapInitiative[]}>}>} goals - The goals to calculate the timeline bounds for
 * @param {dayjs.Dayjs} today - The today's date
 * @param {number | null} selectedYear - The selected year for filtering (null means all years)
 * @returns {TimelineBounds} The timeline bounds
 */
const calculateTimelineBounds = (
  goals: Array<{
    outcomes: Array<{
      initiatives: RoadmapInitiative[];
    }>;
  }>,
  selectedYear?: number | null,
): TimelineBounds => {
  const startDates: dayjs.Dayjs[] = [];
  const endDates: dayjs.Dayjs[] = [];

  goals.forEach((goal) => {
    goal.outcomes.forEach((outcome) => {
      outcome.initiatives.forEach((initiative) => {
        if (initiative.start_date) {
          const startDate = safeParseDateInput(initiative.start_date);
          // Filter by year if specified
          if (startDate && (!selectedYear || startDate.year() === selectedYear)) {
            startDates.push(startDate);
          }
        }
        if (initiative.end_date) {
          const endDate = safeParseDateInput(initiative.end_date);
          // Filter by year if specified
          if (endDate && (!selectedYear || endDate.year() === selectedYear)) {
            endDates.push(endDate);
          }
        }
      });
    });
  });

  // If a specific year is selected, always show the full calendar year (Jan 1 - Dec 31)
  if (selectedYear) {
    const yearStart = dayjs(`${selectedYear}-01-01`).startOf('year');
    const yearEnd = dayjs(`${selectedYear}-12-31`).endOf('year');
    const totalDays = yearEnd.diff(yearStart, 'day') + 1;
    return {
      timelineStart: yearStart,
      timelineEnd: yearEnd,
      totalDays: totalDays,
    };
  }

  if (startDates.length === 0) {
    // If no year selected and no initiatives, show current year
    const yearStart = dayjs().startOf('year');
    const yearEnd = dayjs().endOf('year');
    const totalDays = yearEnd.diff(yearStart, 'day') + 1;
    return {
      timelineStart: yearStart,
      timelineEnd: yearEnd,
      totalDays: totalDays,
    };
  }

  // Find earliest start date
  const start = startDates.reduce((min, date) => (date.isBefore(min) ? date : min));
  // Find latest end date
  let end =
    endDates.length > 0 ? endDates.reduce((max, date) => (date.isAfter(max) ? date : max)) : start.add(6, 'month');

  // Ensure we show the full year - start from January and end at December
  const startYear = start.year();
  const endYear = end.year();

  const timelineStart = dayjs(`${startYear}-01-01`).startOf('year');
  const timelineEnd = dayjs(`${endYear}-12-31`).endOf('year');
  const totalDays = timelineEnd.diff(timelineStart, 'day') + 1;

  return {
    timelineStart,
    timelineEnd,
    totalDays,
  };
};

/**
 * Get the effective end date for an initiative
 * If end date exists, return it; otherwise default to start + 30 days
 * @param {dayjs.Dayjs} start - The start date
 * @param {dayjs.Dayjs | null} end - The end date (optional)
 * @returns {dayjs.Dayjs} The effective end date
 */
const getEffectiveEndDate = (start: dayjs.Dayjs, end: dayjs.Dayjs | null): dayjs.Dayjs => {
  return end || start.add(30, 'day');
};

/**
 * Calculate the position of the initiative bar as percentages
 * @param {RoadmapInitiative} initiative - The initiative to calculate the position for
 * @param {dayjs.Dayjs} timelineStart - The start of the timeline
 * @param {number} totalDays - The total number of days in the timeline
 * @returns {InitiativeBarPosition | null} The position of the initiative bar with percentage values
 */
const calculateInitiativeBarPosition = (
  initiative: RoadmapInitiative,
  timelineStart: dayjs.Dayjs,
  totalDays: number,
): InitiativeBarPosition | null => {
  if (!initiative.start_date) return null;
  if (totalDays <= 0) return null;

  const start = safeParseDateInput(initiative.start_date);
  if (!start) return null;

  const end = initiative.end_date ? safeParseDateInput(initiative.end_date) : null;
  const effectiveEnd = getEffectiveEndDate(start, end);

  const daysFromStart = start.diff(timelineStart, 'day');
  const barDuration = effectiveEnd.diff(start, 'day');

  const leftPercent = (daysFromStart / totalDays) * 100;
  const widthPercent = Math.max((barDuration / totalDays) * 100, 2); // Minimum 2% width

  return { left: leftPercent, width: widthPercent };
};

/**
 * Calculate gridline positions for each quarter as percentages
 * @param {dayjs.Dayjs} timelineStart - The start of the timeline
 * @param {dayjs.Dayjs} timelineEnd - The end of the timeline
 * @param {number} totalDays - The total number of days in the timeline
 * @returns {Array<{leftPercent: number, label: string}>} Array of gridline positions as percentages for quarters
 */
const calculateQuarterlinePositions = (
  timelineStart: dayjs.Dayjs,
  timelineEnd: dayjs.Dayjs,
  totalDays: number,
): Array<{ leftPercent: number; label: string }> => {
  if (totalDays <= 0) return [];

  const gridlines: Array<{ leftPercent: number; label: string }> = [];
  let currentDate = timelineStart.startOf('year');
  const endDate = timelineEnd.endOf('year');

  while (currentDate.isBefore(endDate) || currentDate.isSame(endDate)) {
    const quarter = Math.floor(currentDate.month() / 3) + 1;
    const year = currentDate.year();

    const daysFromStart = currentDate.diff(timelineStart, 'day');
    const leftPercent = (daysFromStart / totalDays) * 100;

    gridlines.push({
      leftPercent,
      label: `Q${quarter} ${year}`,
    });

    // Move to next quarter (3 months)
    currentDate = currentDate.add(3, 'month');
  }

  return gridlines;
};

/**
 * Calculate gridline positions for each month as percentages
 * @param {dayjs.Dayjs} timelineStart - The start of the timeline
 * @param {dayjs.Dayjs} timelineEnd - The end of the timeline
 * @param {number} totalDays - The total number of days in the timeline
 * @returns {Array<{leftPercent: number, label: string}>} Array of gridline positions as percentages
 */
const calculateGridlinePositions = (
  timelineStart: dayjs.Dayjs,
  timelineEnd: dayjs.Dayjs,
  totalDays: number,
): Array<{ leftPercent: number; label: string }> => {
  if (totalDays <= 0) return [];

  const gridlines: Array<{ leftPercent: number; label: string }> = [];
  let currentMonth = timelineStart.startOf('month');
  const endMonth = timelineEnd.endOf('month');

  while (currentMonth.isBefore(endMonth) || currentMonth.isSame(endMonth, 'month')) {
    const daysFromStart = currentMonth.diff(timelineStart, 'day');
    const leftPercent = (daysFromStart / totalDays) * 100;
    gridlines.push({
      leftPercent,
      label: currentMonth.format('MMM YYYY'),
    });
    currentMonth = currentMonth.add(1, 'month');
  }

  return gridlines;
};

/**
 * Calculate the position of a milestone as a percentage
 * @param {string} targetDate - The target end date of the milestone
 * @param {dayjs.Dayjs} timelineStart - The start of the timeline
 * @param {number} totalDays - The total number of days in the timeline
 * @returns {number | null} The position of the milestone as a percentage, or null if outside timeline
 */
const calculateMilestonePosition = (
  targetDate: string,
  timelineStart: dayjs.Dayjs,
  totalDays: number,
): number | null => {
  if (!targetDate) return null;
  if (totalDays <= 0) return null;

  const milestoneDate = safeParseDateInput(targetDate);
  if (!milestoneDate) return null;

  const daysFromStart = milestoneDate.diff(timelineStart, 'day');
  const leftPercent = (daysFromStart / totalDays) * 100;

  // Only return if within visible timeline (0-100%)
  if (leftPercent < 0 || leftPercent > 100) return null;

  return leftPercent;
};

/**
 * Calculate activity date extensions for an initiative
 * Returns extensions if activity dates fall outside planned dates
 * @param {RoadmapInitiative} initiative - The initiative to calculate extensions for
 * @param {dayjs.Dayjs} timelineStart - The start of the timeline
 * @param {number} totalDays - The total number of days in the timeline
 * @returns {Object | null} Object with leftExtension and rightExtension positions, or null if no extensions
 */
const calculateActivityExtensions = (
  initiative: RoadmapInitiative,
  timelineStart: dayjs.Dayjs,
  totalDays: number,
): {
  leftExtension: InitiativeBarPosition | null;
  rightExtension: InitiativeBarPosition | null;
} | null => {
  // Need planned start date to calculate extensions
  if (!initiative.start_date) return null;
  if (totalDays <= 0) return null;

  const plannedStart = safeParseDateInput(initiative.start_date);
  const plannedEnd = initiative.end_date ? safeParseDateInput(initiative.end_date) : null;
  if (!plannedStart) return null;
  if (initiative.end_date && !plannedEnd) return null;

  const activityStart = initiative.activity_start_date ? safeParseDateInput(initiative.activity_start_date) : null;
  if (initiative.activity_start_date && !activityStart) return null;

  const activityEnd = initiative.activity_end_date ? safeParseDateInput(initiative.activity_end_date) : null;
  if (initiative.activity_end_date && !activityEnd) return null;

  let leftExtension: InitiativeBarPosition | null = null;
  let rightExtension: InitiativeBarPosition | null = null;

  // Check for left extension (activity started before planned start)
  if (activityStart && activityStart.isBefore(plannedStart)) {
    const daysFromStart = activityStart.diff(timelineStart, 'day');
    const extensionDuration = plannedStart.diff(activityStart, 'day');

    const left = (daysFromStart / totalDays) * 100;
    const width = Math.max((extensionDuration / totalDays) * 100, 2);

    leftExtension = { left, width };
  }

  // Check for right extension (activity ended after planned end)
  // Use effective planned end (defaults to start + 30 days if no end date)
  const effectivePlannedEnd = getEffectiveEndDate(plannedStart, plannedEnd);
  if (activityEnd && activityEnd.isAfter(effectivePlannedEnd)) {
    const daysFromStart = effectivePlannedEnd.diff(timelineStart, 'day');
    const extensionDuration = activityEnd.diff(effectivePlannedEnd, 'day');

    const left = (daysFromStart / totalDays) * 100;
    const width = Math.max((extensionDuration / totalDays) * 100, 2);

    rightExtension = { left, width };
  }

  // Return null if no extensions are needed
  if (!leftExtension && !rightExtension) return null;

  return { leftExtension, rightExtension };
};

/**
 * Calculate forecast line position for an initiative
 * Returns a line from activity_end_date to forecasted_end_date if forecast extends beyond activity
 * @param {RoadmapInitiative} initiative - The initiative to calculate forecast line for
 * @param {dayjs.Dayjs} timelineStart - The start of the timeline
 * @param {number} totalDays - The total number of days in the timeline
 * @returns {Object | null} Object with line position (left, width) or null if no forecast extension needed
 */
const calculateForecastLinePosition = (
  initiative: RoadmapInitiative,
  timelineStart: dayjs.Dayjs,
  totalDays: number,
): InitiativeBarPosition | null => {
  // Need activity_end_date to start the forecast line from
  if (!initiative.activity_end_date) return null;
  if (totalDays <= 0) return null;

  // Extract forecasted end date (95% confidence)
  const forecastedDate = initiative.forecasts?.forecasted_end_dates?.recommended?.['95_percent'];
  if (!forecastedDate) return null;

  const activityEnd = safeParseDateInput(initiative.activity_end_date);
  const forecast = safeParseDateInput(forecastedDate);

  if (!activityEnd || !forecast) return null;

  // Only show forecast line if it extends beyond activity_end_date
  if (!forecast.isAfter(activityEnd)) return null;

  const daysFromStart = activityEnd.diff(timelineStart, 'day');
  const forecastDuration = forecast.diff(activityEnd, 'day');

  const left = (daysFromStart / totalDays) * 100;
  const width = Math.max((forecastDuration / totalDays) * 100, 2);

  return { left, width };
};

export {
  calculateActivityExtensions,
  calculateForecastLinePosition,
  calculateGridlinePositions,
  calculateInitiativeBarPosition,
  calculateMilestonePosition,
  calculateQuarterlinePositions,
  calculateTimelineBounds,
  matchesDeliveryProgressFilter,
  matchesYearFilter,
};
